import type { DocumentBlock } from "@/engine/types";

/** 붙여 넣기·본문 저장이 거절하는 크기. ADV-E8. */
export const PASTE_MAX_CHARS = 2_000_000;
export const PASTE_MAX_BLOCKS = 2_000;

const ATTACHMENT_PREFIX = "attachment:";

type Inline = {
  type?: string;
  text?: string;
  styles?: Record<string, unknown>;
  props?: Record<string, unknown>;
  content?: Inline[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function asInline(value: unknown): Inline | null {
  return isRecord(value) ? (value as Inline) : null;
}

/** 칩 라벨은 넣지 않는다. 링크 안의 글자만 잇는다. */
export function plainTextFromBlocks(blocks: readonly DocumentBlock[]): string {
  return blocks.map(blockText).filter((line) => line.length > 0).join("\n");
}

function blockText(block: DocumentBlock): string {
  const parts: string[] = [];
  pushContentText(block.content, parts);
  for (const child of block.children ?? []) {
    const nested = blockText(child);
    if (nested) parts.push(nested);
  }
  return parts.join("");
}

function pushContentText(content: unknown, parts: string[]) {
  if (typeof content === "string") {
    parts.push(content);
    return;
  }
  if (Array.isArray(content)) {
    for (const item of content) pushInlineText(asInline(item), parts);
    return;
  }
  if (!isRecord(content)) return;
  const rows = content.rows;
  if (!Array.isArray(rows)) return;
  for (const row of rows) {
    if (!isRecord(row) || !Array.isArray(row.cells)) continue;
    for (const cell of row.cells) {
      if (!isRecord(cell)) continue;
      pushContentText(cell.content, parts);
    }
  }
}

function pushInlineText(item: Inline | null, parts: string[]) {
  if (!item) return;
  if (item.type === "mention") return;
  if (item.type === "link" && Array.isArray(item.content)) {
    for (const child of item.content) pushInlineText(asInline(child), parts);
    return;
  }
  if (typeof item.text === "string") parts.push(item.text);
}

export type BlockSpot = {
  id?: string;
  type?: string;
  text: string;
  mentions: Array<{ targetKind: string; targetId: string }>;
};

/** 문서 순서. 자식 블록은 부모 다음에 온다. 글자는 그 블록의 내용만이다. */
export function blockSpots(blocks: readonly DocumentBlock[]): BlockSpot[] {
  const out: BlockSpot[] = [];
  const visit = (block: DocumentBlock) => {
    const mentions: BlockSpot["mentions"] = [];
    visitContent(block.content, (item) => {
      if (item.type !== "mention") return;
      const targetKind = stringProp(item.props, "targetKind");
      const targetId = stringProp(item.props, "targetId");
      if (!targetKind || !targetId) return;
      mentions.push({ targetKind, targetId });
    });
    const text = ownBlockText(block);
    out.push({
      ...(block.id ? { id: block.id } : {}),
      ...(block.type ? { type: block.type } : {}),
      text,
      mentions,
    });
    for (const child of block.children ?? []) visit(child);
  };
  for (const block of blocks) visit(block);
  return out;
}

function ownBlockText(block: DocumentBlock): string {
  const parts: string[] = [];
  pushContentText(block.content, parts);
  return parts.join("");
}

/** 그 대상을 언급한 첫 블록. 블록 id가 없으면 건너뛴다. */
export function mentionAnchor(blocks: readonly DocumentBlock[], targetKind: string, targetId: string): string | undefined {
  for (const spot of blockSpots(blocks)) {
    if (!spot.id) continue;
    if (spot.mentions.some((item) => item.targetKind === targetKind && item.targetId === targetId)) return spot.id;
  }
  return undefined;
}

/**
 * 체크 항목을 문단으로 내린다. 체크 여부는 작업 상태가 되지 않으므로 버린다.
 * 그 블록이 체크가 아니면 null이다.
 */
export function releaseCheckItem(blocks: readonly DocumentBlock[], blockId: string): DocumentBlock[] | null {
  let found = false;
  const walk = (block: DocumentBlock): DocumentBlock => {
    if (block.id === blockId && block.type === "checkListItem") {
      found = true;
      const props = { ...(block.props ?? {}) };
      delete props.checked;
      const { props: _dropped, ...rest } = block;
      return { ...rest, type: "paragraph", ...(Object.keys(props).length ? { props } : {}) };
    }
    if (!block.children?.length) return block;
    return { ...block, children: block.children.map(walk) };
  };
  const next = blocks.map(walk);
  return found ? next : null;
}

export function collectMentions(blocks: readonly DocumentBlock[]): Array<{ targetKind: string; targetId: string }> {
  const seen = new Set<string>();
  const out: Array<{ targetKind: string; targetId: string }> = [];
  const visit = (block: DocumentBlock) => {
    visitContent(block.content, (item) => {
      if (item.type !== "mention") return;
      const targetKind = stringProp(item.props, "targetKind");
      const targetId = stringProp(item.props, "targetId");
      if (!targetKind || !targetId) return;
      const key = `${targetKind}:${targetId}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ targetKind, targetId });
    });
    for (const child of block.children ?? []) visit(child);
  };
  for (const block of blocks) visit(block);
  return out;
}

function visitContent(content: unknown, visit: (item: Inline) => void) {
  if (Array.isArray(content)) {
    for (const item of content) {
      const inline = asInline(item);
      if (!inline) continue;
      visit(inline);
      if (Array.isArray(inline.content)) visitContent(inline.content, visit);
    }
    return;
  }
  if (!isRecord(content) || !Array.isArray(content.rows)) return;
  for (const row of content.rows) {
    if (!isRecord(row) || !Array.isArray(row.cells)) continue;
    for (const cell of row.cells) {
      if (isRecord(cell)) visitContent(cell.content, visit);
    }
  }
}

function stringProp(props: Record<string, unknown> | undefined, key: string): string {
  const value = props?.[key];
  return typeof value === "string" ? value : "";
}

/** 하이라이트 댓글 마크는 P2 전이라 저장본에 남기지 않는다. */
export function stripCommentMarks(blocks: readonly DocumentBlock[]): DocumentBlock[] {
  return blocks.map((block) => mapBlock(block, (item) => item));
}

/** 연결 해제 때 칩을 지우고 그 자리의 라벨 평문을 남긴다. */
export function replaceMentionWithText(blocks: readonly DocumentBlock[], targetKind: string, targetId: string, label: string): DocumentBlock[] {
  return blocks.map((block) => mapBlock(block, (item) => {
    if (item.type === "mention" && stringProp(item.props, "targetKind") === targetKind && stringProp(item.props, "targetId") === targetId) {
      return { type: "text", text: label, styles: {} };
    }
    return item;
  }));
}

export function appendMention(blocks: readonly DocumentBlock[], blockId: string | undefined, targetKind: string, targetId: string): DocumentBlock[] {
  const mention: Inline = { type: "mention", props: { targetKind, targetId } };
  const space: Inline = { type: "text", text: " ", styles: {} };
  if (!blocks.length) {
    return [{ id: blockId, type: "paragraph", content: [mention] }];
  }
  let placed = false;
  const next = blocks.map((block) => {
    if (placed) return block;
    if (blockId && block.id !== blockId) return block;
    if (!blockId && block !== blocks[blocks.length - 1]) return block;
    placed = true;
    const content = Array.isArray(block.content) ? [...block.content, space, mention] : [mention];
    return { ...block, content };
  });
  if (placed) return next;
  const last = next[next.length - 1]!;
  const content = Array.isArray(last.content) ? [...last.content, space, mention] : [mention];
  return [...next.slice(0, -1), { ...last, content }];
}

function mapBlock(block: DocumentBlock, mapInline: (item: Inline) => Inline): DocumentBlock {
  return {
    ...block,
    content: mapContent(block.content, mapInline),
    children: block.children?.map((child) => mapBlock(child, mapInline)),
  };
}

function mapContent(content: unknown, mapInline: (item: Inline) => Inline): unknown {
  if (Array.isArray(content)) return content.map((item) => mapInlineNode(item, mapInline));
  if (!isRecord(content) || !Array.isArray(content.rows)) return content;
  return {
    ...content,
    rows: content.rows.map((row) => {
      if (!isRecord(row) || !Array.isArray(row.cells)) return row;
      return {
        ...row,
        cells: row.cells.map((cell) => (isRecord(cell) ? { ...cell, content: mapContent(cell.content, mapInline) } : cell)),
      };
    }),
  };
}

function mapInlineNode(item: unknown, mapInline: (item: Inline) => Inline): unknown {
  const inline = asInline(item);
  if (!inline) return item;
  const next = mapInline(stripCommentStyle(inline));
  if (Array.isArray(next.content)) return { ...next, content: next.content.map((child) => mapInlineNode(child, mapInline)) };
  return next;
}

function stripCommentStyle(item: Inline): Inline {
  if (!item.styles || !("comment" in item.styles)) return item;
  const styles = { ...item.styles };
  delete styles.comment;
  return { ...item, styles };
}

export function blocksContainDataUrl(blocks: readonly DocumentBlock[]): boolean {
  return JSON.stringify(blocks).includes("data:");
}

export function countBlocks(blocks: readonly DocumentBlock[]): number {
  let n = 0;
  const walk = (list: readonly DocumentBlock[]) => {
    for (const block of list) {
      n += 1;
      if (block.children?.length) walk(block.children);
    }
  };
  walk(blocks);
  return n;
}

/** 빠진 첨부의 이미지 블록은 바이트 없이 안내 문단이 된다. */
export function detachMissingImages(blocks: readonly DocumentBlock[], attachmentIds: ReadonlySet<string>): DocumentBlock[] {
  return blocks.map((block) => {
    if (block.type === "image") {
      const url = stringProp(block.props, "url");
      const id = url.startsWith(ATTACHMENT_PREFIX) ? url.slice(ATTACHMENT_PREFIX.length) : "";
      if (!id || !attachmentIds.has(id) || url.startsWith("data:")) {
        return { id: block.id, type: "paragraph", content: [{ type: "text", text: "첨부를 뺐습니다", styles: {} }] };
      }
    }
    return {
      ...block,
      children: block.children ? detachMissingImages(block.children, attachmentIds) : block.children,
    };
  });
}

export function attachmentUrl(id: string): string {
  return `${ATTACHMENT_PREFIX}${id}`;
}

export function isBlankBlocks(blocks: readonly DocumentBlock[]): boolean {
  return plainTextFromBlocks(blocks).trim().length === 0 && !blocks.some(hasStructure);
}

function hasStructure(block: DocumentBlock): boolean {
  if (block.type && block.type !== "paragraph") return true;
  return (block.children ?? []).some(hasStructure);
}
